
import {
  defineComponent,
  ref,
  computed,
  PropType,
  watch,
  watchEffect,
  nextTick,
} from 'vue';
import { useFuzzyQueryUser } from './use-fuzzy-query-user';
import { _CreatedUser } from '../composables/index';
import { IAddUserInfoParams } from '@/api/manage-app/user-info/index.d';
import { GetV1AccountInfoApiResItem } from '@/api/manage-app/account-info/index.d';
import { APP_USER_SOURCE, REBUILD_APP_ENUM, REBUILD_APP_LIST } from '@/constant';
import { useAppList } from '@/views/app/app-manage/composables/useAppList';

interface IPageAddUserInfoParams extends IAddUserInfoParams {
  userId?: string;
}

export default defineComponent({
  name: 'CreatedUser',
  emits: ['update:visible', 'handleSearch'],
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    accountUserItem: {
      type: Object as PropType<IAddUserInfoParams>,
      default: () => ({}),
    },
    accountList: {
      type: Array as PropType<GetV1AccountInfoApiResItem[]>,
      default: () => [],
    },
  },
  setup(props, { emit }) {
    const selfVisible = computed({
      get() {
        return props.visible;
      },
      set(val: boolean) {
        emit('update:visible', val);
      },
    });
    const formData = ref<IPageAddUserInfoParams>({
      accountName: '', // 开发者账号
      accountId: '', // 开发者帐号Id
      accountInfoId: '', // 主键
      userCode: '', // 用户编码
      userName: '', // 用户名称
      userId: '', // 用户ID
      userUDID: '', // UDID
      dataChannel: '', // 用户来源
      isReBuild: REBUILD_APP_ENUM.YES, // 是否重新构建App
      appCode: '', // app编码
      companyId: '', // 租户id
    });

    // 创建人员
    const {
      userList,
      fuzzyQueryUserLoading,
      handleFuzzyQueryUser,
    } = useFuzzyQueryUser(false, formData);

    const {
      handleSave,
      handleClose,
      accountList,
      getAccountLists,
    } = _CreatedUser(
      formData,
      emit as any,
    );

    // 开发者账号搜索
    const options = ref<any>([]);
    const querySearchAsync = async () => {
      const data = {
        accountName: '',
      };
      await getAccountLists(data);
      options.value = accountList.value;
    };
    querySearchAsync();

    watch(() => props.accountUserItem, (val) => {
      if (val.id) {
        formData.value = val;
        formData.value.isReBuild = REBUILD_APP_ENUM.NO;
        nextTick(() => {
          userList.value = [
            { userId: formData.value.userId!, name: formData.value.userName, bbCode: formData.value.userCode },
          ];
        });
      } else {
        formData.value = Object.assign({}, {
          accountInfoId: '',
          accountName: '', // 开发者账号
          accountId: '', // 开发者帐号Id
          userCode: '', // 用户编码
          userName: '', // 用户名称
          userId: '', // 用户id
          userUDID: '', // UDID
          dataChannel: '', // 用户来源
          isReBuild: REBUILD_APP_ENUM.YES, // 是否重新构建App
          appCode: '', // app编码
          companyId: '', // 租户id
        });
      }
    });

    watchEffect(() => {
      if (formData.value.accountId && options.value.length) {
        const currentAccount = options.value.find((item: GetV1AccountInfoApiResItem) => {
          return item.accountId === formData.value.accountId;
        });
        if (currentAccount) {
          formData.value.accountName = currentAccount.accountName;
          formData.value.accountInfoId = currentAccount.id;
        }
      }
      if (formData.value.userId && userList.value.length) {
        const currentUser = userList.value.find((item) => {
          return item.userId === formData.value.userId;
        });
        if (currentUser) {
          formData.value.userCode = currentUser.bbCode!;
          formData.value.userName = currentUser.name;
        }
      }
    });

    const handleChangeSource = () => {
      formData.value.userName = '';
      formData.value.userCode = '';
      formData.value.userId = '';
    };

    const { appList, getAppListFn } = useAppList({
      accountId: formData.value.accountId,
    });
    const appListOption = computed(() => {
      return [
        {
          appCode: 'All',
          appName: '全部',
        },
        ...appList.value,
      ];
    });

    const handleAccountIdChange = async () => {
      formData.value.appCode = '';
      await nextTick();
      getAppListFn({
        accountId: formData.value.accountInfoId,
      });
    };

    const handleOpen = () => {
      handleAccountIdChange();
    };

    const handleTips = (item: GetV1AccountInfoApiResItem) => {
      if (item.holder) {
        const holderList = item.holder?.split('_');
        if (holderList.length > 1) {
          return holderList.splice(1).join('_');
        }
      }
      return '';
    };

    return {
      handleTips,
      formData,
      selfVisible,
      appListOption,
      rules: {
        accountId: [
          { required: true, message: '请选择开发者账号', trigger: 'change' },
        ],
        dataChannel: [
          { required: true, message: '请选择用户来源', trigger: 'blur' },
        ],
        userId: [
          { required: true, message: '请输入用户名称', trigger: 'blur' },
        ],
        userUDID: [
          { required: true, message: '请输入UDID', trigger: 'blur' },
        ],
        isReBuild: [
          { required: true, message: '请选择是否重新构建App', trigger: 'change' },
        ],
        appCode: [
          { required: true, message: '请选择选择构建的App', trigger: 'change' },
        ],
      },
      userList,
      fuzzyQueryUserLoading,
      handleFuzzyQueryUser,

      handleSave,
      handleClose,
      handleOpen,
      querySearchAsync,
      options,
      SOURCE: [
        { name: 'SSO', code: APP_USER_SOURCE.SSO },
        { name: '百布', code: APP_USER_SOURCE.BAIBU },
      ],
      handleChangeSource,
      REBUILD_APP_ENUM,
      REBUILD_APP_LIST,
      handleAccountIdChange,
    };
  },
});
